import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Loader2 } from 'lucide-react';

/**
 * SubscriptionGuard - Protects routes based on subscription status
 * 
 * Usage: Wrap routes that require a valid subscription
 * 
 * Flow:
 * - If user is admin -> allow access (no subscription needed)
 * - If user has valid subscription (active, trial, gifted) -> allow access  
 * - Otherwise -> redirect to /subscription
 */
const SubscriptionGuard = ({ children }) => {
  const { user, loading, hasSubscriptionAccess } = useAuth();
  const location = useLocation();

  // Show loading while checking auth
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-orange-600 mx-auto mb-4" />
          <p className="text-gray-600">Laden...</p>
        </div>
      </div>
    );
  }

  // If not logged in, redirect to login
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Admins ALWAYS have access - double check here
  if (user.role === 'admin') {
    return children;
  }

  // Check subscription access for non-admins
  if (!hasSubscriptionAccess()) {
    return <Navigate to="/company/subscription" state={{ from: location }} replace />;
  }

  // User has access, render children
  return children;
};

export default SubscriptionGuard;
